import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import './FloatingActionButton.css';

interface FloatingActionButtonProps {
  icon: 'add-note' | 'add-document' | 'add-folder' | 'add-storyboard';
  onClick: () => void;
  position?: 'primary' | 'secondary';
}

export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({ 
  icon, 
  onClick,
  position = 'primary'
}) => {
  const { t } = useLanguage();
  // What this button adds, said properly in the reader's language rather
  // than patched together from the icon name.
  const labels: Record<FloatingActionButtonProps['icon'], string> = {
    'add-note': t.nav.addNote,
    'add-document': t.nav.addDocument,
    'add-folder': t.nav.addFolder,
    'add-storyboard': t.nav.addStoryboard,
  };

  const renderIcon = () => {
    switch (icon) {
      case 'add-note':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
            <path d="M2 2l7.586 7.586"></path>
            <circle cx="11" cy="11" r="2"></circle>
          </svg>
        );
      case 'add-document':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="12" y1="18" x2="12" y2="12"></line>
            <line x1="9" y1="15" x2="15" y2="15"></line>
          </svg>
        );
      case 'add-folder':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            <line x1="12" y1="11" x2="12" y2="17"></line>
            <line x1="9" y1="14" x2="15" y2="14"></line>
          </svg>
        );
      case 'add-storyboard':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
            <line x1="12" y1="19" x2="12" y2="16"></line>
            <line x1="10.5" y1="17.5" x2="13.5" y2="17.5"></line>
          </svg>
        );
    }
  };

  return (
    <button 
      className={`floating-action-button ${position}`}
      onClick={onClick}
      aria-label={labels[icon]}
    >
      {renderIcon()}
    </button>
  );
};
